import { useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { api } from '@/api/client';
import { useAuth } from '@/hooks/useAuth';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { usePincodeAutofill } from '@/hooks/usePincodeAutofill';
import { useServiceabilityCheck } from '@/hooks/useServiceabilityCheck';
import { CustomerLookupCard } from '@/components/order/CustomerLookupCard';
import { OrderValidationPanel, ruleCheck, type ValidatableOrder } from './OrderValidationPanel';
import { QuickCreateMerchant, QuickCreateCourier, QuickCreatePickup } from './QuickCreateButtons';

interface Merchant { id: number; companyName: string }
interface Courier { id: number; name: string; orderType: 'B2C' | 'B2B' }
interface State { id: number; name: string }
interface Warehouse { id: number; name: string; merchantId?: number }

// Keystroke guards for the booking form's numeric fields.
// onlyDigitKeys → mobile / pincode / qty (whole numbers only, no letters or symbols).
// blockNonNumericKeys → invoice value / weight / dimensions (decimals allowed, but no e/E/+/-).
const onlyDigitKeys = (e: any) => { if (e.ctrlKey || e.metaKey || e.altKey) return; if (e.key.length === 1 && !/[0-9]/.test(e.key)) e.preventDefault(); };
const blockNonNumericKeys = (e: any) => { if (e.ctrlKey || e.metaKey || e.altKey) return; if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault(); };

interface FormData {
  // How the AWB is obtained. AUTOMATIC = book with the courier's API and let it allocate the number.
  // MANUAL = the AWB is typed in here and stored exactly as entered; the courier is never contacted.
  bookingMode: 'AUTOMATIC' | 'MANUAL';
  awbNo?: string;
  bookingType?: string;
  merchantId: number | '';
  orderNo?: string;
  invoiceNo?: string;
  deliveryType: 'COD' | 'PREPAID';
  codValue?: number;
  courierId?: number | '';
  warehouseId?: number | '';

  customerMobile: string;
  customerName: string;
  customerEmail?: string;
  customerAddress: string;
  customerPincode: string;
  customerCity: string;
  customerState: string;

  products: Array<{ name?: string; project?: string; qty?: number; invoiceValue?: number; ewayBillNo?: string }>;

  weight?: number;
  length?: number;
  breadth?: number;
  height?: number;
  isFragile?: 'Yes' | 'No' | '';
}

// Build create-form defaults from an existing order when "Duplicate Order" is used.
// Copies everything EXCEPT identifiers that must stay unique (AWB / LR are never carried over).
function b2cDefaultsFromOrder(o: any): Partial<FormData> {
  const dim: any = (o?.dimensions && !Array.isArray(o.dimensions)) ? o.dimensions : {};
  const products = Array.isArray(o?.products) && o.products.length
    ? o.products.map((p: any) => ({
        name: p?.name ?? '',
        project: p?.project ?? '',
        qty: Number(p?.qty ?? 1) || 1,
        invoiceValue: p?.value != null ? Number(p.value) : (p?.invoiceValue != null ? Number(p.invoiceValue) : undefined),
        ewayBillNo: p?.ewayBillNo ?? '',
      }))
    : [{ qty: 1 }];
  return {
    // A duplicate keeps the source order's booking mode but NEVER its AWB — that number belongs to
    // the original shipment and is unique in the database.
    bookingMode: o?.bookingMode === 'MANUAL' ? 'MANUAL' : 'AUTOMATIC',
    awbNo: '',
    bookingType: o?.bookingType ?? '',
    merchantId: o?.merchantId ?? '',
    orderNo: o?.orderNo ?? '',
    invoiceNo: o?.invoiceNo ?? '',
    deliveryType: o?.deliveryType === 'COD' ? 'COD' : 'PREPAID',
    codValue: o?.deliveryType === 'COD' ? (Number(o?.codValue ?? 0) || 0) : 0,
    courierId: o?.courierId ?? '',
    warehouseId: o?.warehouseId ?? '',
    customerMobile: o?.customerMobile ?? '',
    customerName: o?.customerName ?? '',
    customerEmail: o?.customerEmail ?? '',
    customerAddress: o?.customerAddress ?? '',
    customerPincode: o?.customerPincode ?? '',
    customerCity: o?.customerCity ?? '',
    customerState: o?.customerState ?? '',
    products,
    weight: dim?.weight != null ? Number(dim.weight) : (o?.chargeableWeight != null ? Number(o.chargeableWeight) : undefined),
    length: dim?.length != null ? Number(dim.length) : undefined,
    breadth: dim?.breadth != null ? Number(dim.breadth) : undefined,
    height: dim?.height != null ? Number(dim.height) : undefined,
    isFragile: dim?.isFragile === true ? 'Yes' : (dim?.isFragile === false ? 'No' : ''),
  };
}

function normalizeB2c(d: any): ValidatableOrder {
  return {
    customerName: d.customerName, customerMobile: d.customerMobile, customerAddress: d.customerAddress,
    customerCity: d.customerCity, customerState: d.customerState, customerPincode: d.customerPincode,
    dimensions: [{ length: d.length, breadth: d.breadth, height: d.height, weight: d.weight }],
  };
}

export function CreateOrderB2cPage() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const loc = useLocation();
  const dup = (loc.state as any)?.duplicateFrom;
  // When arriving from the Orders Bucket "Move to B2C" action, `dup` is the bucket payload pre-fill and
  // bucketId lets us mark that bucket routed once the order is saved.
  const bucketId = (loc.state as any)?.bucketId as string | undefined;

  const { register, handleSubmit, control, watch, setValue, formState: { errors } } = useForm<FormData>({
    defaultValues: dup ? b2cDefaultsFromOrder(dup) : {
      bookingMode: 'AUTOMATIC',
      deliveryType: 'PREPAID',
      codValue: 0,
      products: [{ qty: 1 }],
    },
  });
  const { fields: products, append: addProduct, remove: removeProduct } = useFieldArray({ control, name: 'products' });

  // A merchant logged into their own portal ships only as themselves — auto-select their merchant id
  // and hide the merchant picker (that selector is for admin / internal staff who ship on behalf of many).
  const { user } = useAuth();
  const isMerchant = user?.kind === 'merchant';
  const myMerchantId = isMerchant ? user?.id : undefined;
  useEffect(() => {
    if (isMerchant && myMerchantId) setValue('merchantId', myMerchantId as any, { shouldValidate: true });
  }, [isMerchant, myMerchantId, setValue]);

  const { data: merchants } = useQuery({ queryKey: ['merchants', 'B2C'], enabled: !isMerchant, queryFn: async () => (await api.get<Merchant[]>('/merchants', { params: { serviceType: 'B2C' } })).data });
  const { data: couriers } = useQuery({ queryKey: ['couriers', 'B2C-active'], queryFn: async () => (await api.get<Courier[]>('/couriers', { params: { orderType: 'B2C', isActive: 'true' } })).data });
  const { data: states } = useQuery({ queryKey: ['states'], queryFn: async () => (await api.get<State[]>('/states')).data });

  const selectedMerchant = watch('merchantId');
  const { data: assignedCouriers } = useQuery({
    queryKey: ['merchant-couriers', selectedMerchant],
    enabled: !!selectedMerchant,
    queryFn: async () => (await api.get<any[]>(`/merchants/${selectedMerchant}/couriers`, { params: { activeOnly: 1 } })).data,
  });
  // Filter to B2C or BOTH couriers, fall back to all if none assigned
  const courierOptions = (() => {
    const allowed = (assignedCouriers ?? []).map((mc: any) => mc.courier).filter((c: any) => !c.orderType || c.orderType === 'B2C' || c.orderType === 'BOTH');
    if (selectedMerchant && allowed.length > 0) return allowed;
    return couriers ?? [];
  })();
  const watchDeliveryType = watch('deliveryType');
  const bookingMode = watch('bookingMode');
  const customerPincode = watch('customerPincode');
  const customerMobile = watch('customerMobile');
  const watchCourierId = watch('courierId');
  const svcCheck = useServiceabilityCheck(watchCourierId, customerPincode);
  // Serviceability blocks a booking we are about to make. A manual AWB records one the courier has
  // ALREADY accepted, so the check is informational there and must not stop the order being saved.
  const notServiceable = !!svcCheck.data?.found && svcCheck.data?.serviceable === false && watch('bookingMode') !== 'MANUAL';
  usePincodeAutofill(customerPincode, (d) => {
    setValue('customerCity', d.city);
    setValue('customerState', d.state);
  });
  const { data: warehouses } = useQuery({
    queryKey: ['merchant-warehouses', selectedMerchant],
    queryFn: async () => (await api.get<Warehouse[]>(`/merchants/${selectedMerchant}/warehouses`, { params: { includeMaster: 'true' } })).data,
    enabled: !!selectedMerchant,
  });

  const length = watch('length'); const breadth = watch('breadth'); const height = watch('height');
  const deadWeight = watch('weight');
  const volWeight = length && breadth && height ? ((length * breadth * height) / 5000).toFixed(2) : 'N/A';
  const volNum = volWeight === 'N/A' ? 0 : Number(volWeight);
  const deadNum = Number(deadWeight ?? 0);
  const chargedWeight = (deadNum > 0 || volNum > 0) ? Math.max(deadNum, volNum).toFixed(2) : 'N/A';

  const create = useMutation({
    mutationFn: async (d: FormData) => {
      const manual = d.bookingMode === 'MANUAL';
      // Serviceability only gates a REAL booking. A manual AWB is bookkeeping for a shipment that is
      // already moving, so blocking it on our serviceability table would refuse to record reality.
      if (!manual && d.courierId && svcCheck.data?.found && svcCheck.data?.serviceable === false) {
        throw new Error(`Destination pincode ${d.customerPincode} is not serviceable by the selected courier. Choose a different courier or pincode.`);
      }
      const payload: any = {
        orderType: 'B2C',
        bookingMode: d.bookingMode,
        // Manual: the number entered here IS the AWB — the order is saved as booked and no courier
        // API call is made. `autoShip:false` stops the create endpoint from booking it.
        awbNo: manual ? (d.awbNo ?? '').trim() : undefined,
        autoShip: !manual,
        merchantId: Number(d.merchantId),
        courierId: d.courierId ? Number(d.courierId) : undefined,
        warehouseId: d.warehouseId ? Number(d.warehouseId) : undefined,
        orderNo: d.orderNo || undefined,
        invoiceNo: (d.invoiceNo ?? '').trim() || undefined,
        deliveryType: d.deliveryType,
        customerName: d.customerName,
        customerMobile: d.customerMobile,
        customerEmail: d.customerEmail || undefined,
        customerAddress: d.customerAddress,
        customerCity: d.customerCity,
        customerState: d.customerState,
        customerPincode: d.customerPincode,
        products: d.products,
        dimensions: { length: d.length, breadth: d.breadth, height: d.height, weight: d.weight, volWeight: volWeight === 'N/A' ? undefined : Number(volWeight), isFragile: d.isFragile === 'Yes' },
        invoiceValue: (d.products ?? []).reduce((s, p) => s + (Number(p.invoiceValue) || 0), 0),
        codValue: d.deliveryType === 'COD' ? Number(d.codValue ?? 0) : 0,
      };
      return (await api.post('/orders', payload)).data;
    },
    onSuccess: async (created: any) => {
      // If this order came from an Orders Bucket entry, mark it routed + link the order (best-effort —
      // the order is already created, so a link failure only leaves the bucket entry Pending).
      if (bucketId) {
        try {
          await api.post(`/orders-bucket/${bucketId}/link`, { orderId: String(created?.id ?? ''), as: 'B2C' });
          qc.invalidateQueries({ queryKey: ['orders-bucket'] });
        } catch (e: any) { toast.error('Order created, but updating the bucket failed: ' + (e?.response?.data?.message ?? 'unknown')); }
      }
      // Force an immediate refetch of the orders list AND tab counts (even while inactive/unmounted) so the
      // new order is already present when we return to the list / Today Uploads tab. A plain invalidate only
      // marks inactive queries stale, which could still show a stale cached page on return.
      qc.invalidateQueries({
        predicate: (q: any) => Array.isArray(q.queryKey) && (q.queryKey[0] === 'orders' || q.queryKey[0] === 'order-tab-counts'),
        refetchType: 'all',
      });
      // A prepaid merchant over their limit still gets the ORDER saved, but it was not booked. Say so
      // loudly and stay put — a plain "Order created" would read as shipped when nothing was shipped.
      if (created?.bookingBlockedReason) {
        toast.warning(created.bookingBlockedReason, { duration: 12000 });
      } else {
        toast.success('Order created');
      }
      nav('/orders-b2c');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to create order'),
  });

  return (
    <div className="p-6 pb-28 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Create Order</h1>
          {bucketId
            ? <p className="text-xs text-amber-700 mt-0.5">Pre-filled from Orders Bucket #{bucketId} — review, complete any missing fields, and Save. The bucket entry is marked routed once saved.</p>
            : dup && <p className="text-xs text-amber-700 mt-0.5">Duplicated from {dup.orderNo ?? dup.systemOrderNo ?? 'order'} — review &amp; edit before saving (AWB/LR not carried over).</p>}
        </div>
        <button onClick={() => nav(-1)} className="px-3 py-2 border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50 flex items-center gap-2">
          <ArrowLeft size={14} /> Back
        </button>
      </div>

      <form onSubmit={handleSubmit((d) => {
        const errs = ruleCheck('B2C', normalizeB2c(d)).filter(i => i.severity === 'error');
        if (errs.length) { toast.error('Fix validation errors: ' + errs.map(e => e.message).join(' ')); return; }
        create.mutate(d);
      })} className="space-y-4">
        <Section title="Order Information">
          <div className="grid grid-cols-3 gap-3">
            <Field label="Booking Type *">
              <select {...register('bookingMode', { required: true })} className="input">
                <option value="AUTOMATIC">Automatic</option>
                <option value="MANUAL">Manual</option>
              </select>
            </Field>
            {bookingMode === 'MANUAL' && (
              <Field label="AWB Number *" error={errors.awbNo?.message}>
                <input {...register('awbNo', {
                  required: 'Required when booking type is Manual',
                  setValueAs: (v: any) => String(v ?? '').trim(),
                  validate: (v) => !!String(v ?? '').trim() || 'Enter the AWB number',
                })} placeholder="AWB already allotted by the courier" className="input font-mono" />
              </Field>
            )}
            {bookingMode === 'MANUAL' && (
              <div className="col-span-3 text-sm rounded-md border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2">
                <b>Manual booking</b> — the AWB above is saved on the order exactly as entered and the courier&apos;s
                booking API is <b>not</b> called. The order goes straight to <b>Booking Received</b>; the courier has no
                record of it from us, so labels, tracking pulls and child AWBs come from whatever the courier already has.
              </div>
            )}
            <Field label="Shipment Type">
              <select {...register('bookingType')} className="input">
                <option value="">Select Shipment Type</option>
                <option value="FORWARD">Forward</option>
                <option value="REVERSE">Reverse</option>
                <option value="EXCHANGE">Exchange</option>
              </select>
            </Field>
            {isMerchant ? (
              <input type="hidden" {...register('merchantId', { required: 'Required' })} />
            ) : (
              <Field label="Select Merchant *" error={errors.merchantId?.message}>
                <input type="hidden" {...register('merchantId', { required: 'Required' })} />
                <SearchableSelect value={selectedMerchant ?? ''} onChange={(v) => setValue('merchantId', v as any, { shouldValidate: true })} options={(merchants ?? []).map(m => ({ value: m.id, label: m.companyName + ' (#' + m.id + ')' }))} placeholder="Select merchant" />
                <QuickCreateMerchant onCreated={(m) => setValue('merchantId', m.id, { shouldValidate: true })} />
              </Field>
            )}
            <Field label="Order ID">
              <input {...register('orderNo')} placeholder="Customer order reference" className="input" />
            </Field>
            {/* A DIFFERENT document from the Order ID above, and the one an ERP keys on. It is sent
                to the merchant's tracking webhook as `invoice_no`; without it that field falls back
                to the order number, which is what made KIPS answer "Record not found for InvoiceId"
                on every update. */}
            <Field label="Invoice No">
              <input {...register('invoiceNo')} placeholder="e.g. SIK-26-000725" className="input" />
            </Field>

            <Field label="Delivery Type *">
              <select {...register('deliveryType', { required: true })} className="input">
                <option value="PREPAID">Prepaid</option>
                <option value="COD">COD</option>
              </select>
            </Field>
            {watchDeliveryType === 'COD' && (
              <Field label="COD Amount * (₹)" error={errors.codValue?.message}>
                <input type="number" step="0.01" min="1" inputMode="decimal" onKeyDown={blockNonNumericKeys}
                  {...register('codValue', { required: 'COD amount is required for COD orders', min: { value: 1, message: 'Must be > 0' }, valueAsNumber: true })}
                  className="input" placeholder="e.g. 1499.00" />
              </Field>
            )}
            {/* Both REQUIRED as of 2026-08-19, by request, matching the B2B form.
                ⚠ This is a WORKFLOW change, not the closing of a hole. Unlike B2B, a blank pickup
                location has been working on B2C: of 1,113 manually-keyed B2C orders in the 30 days
                before this changed, 1,023 went on to be **Delivered** and only 7 sat at Not Shipped
                — the courier resolves pickup from the account when the field is empty. So the cost
                of leaving it blank is a loss of control over WHICH location is used, not a failed
                shipment. Whoever revisits this should know the field is now mandatory because it was
                asked for, not because orders were breaking without it. */}
            <Field label="Select Courier *" error={errors.courierId?.message}>
              <input type="hidden" {...register('courierId', { required: 'Choose a courier — the shipment cannot be booked without one' })} />
              <SearchableSelect value={watch('courierId') ?? ''} onChange={(v) => setValue('courierId', v as any, { shouldValidate: true })} options={courierOptions.map((c: any) => ({ value: c.id, label: c.name + ' (#' + c.id + ')' }))} placeholder="Select Courier" />
              <QuickCreateCourier onCreated={(c) => setValue('courierId', c.id as any, { shouldValidate: true })} />
            </Field>
            <Field label="Pickup Location *" error={errors.warehouseId?.message}>
              <input type="hidden" {...register('warehouseId', { required: 'Pickup location is required — the courier cannot book a shipment without one' })} />
              <SearchableSelect value={watch('warehouseId') ?? ''} onChange={(v) => setValue('warehouseId', v as any, { shouldValidate: true })} options={(warehouses ?? []).map(w => ({ value: w.id, label: w.name + ' (' + w.id + ')' }))} placeholder="Select pickup location" />
              <QuickCreatePickup merchantId={selectedMerchant} onCreated={(w) => setValue('warehouseId', w.id as any, { shouldValidate: true })} />
            </Field>
          </div>
        </Section>

        <Section title="Shipping Information">
          <div className="grid grid-cols-3 gap-3">
            <Field label="Customer Mobile *" error={errors.customerMobile?.message}>
              <input inputMode="numeric" maxLength={10} onKeyDown={onlyDigitKeys} {...register('customerMobile', {
                required: 'Required',
                setValueAs: (v: any) => String(v ?? '').replace(/\D/g, '').slice(-10),
                validate: (v: string) => /^[0-9]{10}$/.test(v) || 'Enter exactly 10 digits (e.g. 9876543210). +91 / spaces / dashes are stripped automatically.',
              })} placeholder="10-digit" className="input" />
            </Field>
            <Field label="Customer Name *" error={errors.customerName?.message}>
              <input {...register('customerName', { required: 'Required' })} className="input" />
            </Field>
            <Field label="Customer Email">
              <input type="email" {...register('customerEmail')} className="input" />
            </Field>

            <Field label="Customer Address *" error={errors.customerAddress?.message}>
              <input {...register('customerAddress', { required: 'Required' })} className="input" />
            </Field>
            <div className="col-span-3"><CustomerLookupCard merchantId={Number(selectedMerchant) || undefined} mobile={customerMobile} onPick={({customer, address}) => { setValue("customerName", customer.name); setValue("customerPincode", address.pincode); setValue("customerCity", address.city); setValue("customerState", address.state); setValue("customerAddress", address.address1); }} /></div>
            <Field label="Pincode *" error={errors.customerPincode?.message}>
              <input maxLength={6} inputMode="numeric" onKeyDown={onlyDigitKeys} {...register('customerPincode', { required: 'Required', pattern: { value: /^\d{6}$/, message: '6 digits' }, setValueAs: (v: any) => String(v ?? '').replace(/\D/g, '').slice(0, 6) })} className="input font-mono" />
            </Field>
            <Field label="City *" error={errors.customerCity?.message}>
              <input {...register('customerCity', { required: 'Required' })} className="input" />
            </Field>

            <Field label="Service State *" error={errors.customerState?.message}>
              <select {...register('customerState', { required: 'Required' })} className="input">
                <option value="">— Select State —</option>
                {states?.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
              </select>
            </Field>
            {notServiceable && (
              <div className="col-span-3 text-sm rounded-md border border-rose-300 bg-rose-50 text-rose-700 px-3 py-2">
                ⛔ This pincode is <b>not serviceable</b> by the selected courier — you can&apos;t book this shipment. Choose a different courier or correct the pincode.
              </div>
            )}
            {!notServiceable && svcCheck.data?.oda && (
              <div className="col-span-3 text-sm rounded-md border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2">
                ⚠ This pincode is <b>ODA (Out of Delivery Area)</b> for the selected courier — extra charges / longer delivery time may apply. You can still book.
              </div>
            )}
          </div>
        </Section>

        <Section title="Product Information" right={
          <button type="button" onClick={() => addProduct({ qty: 1 })} className="text-emerald-700 text-xs border border-emerald-300 px-2 py-1 rounded hover:bg-emerald-50">+ Add Product</button>
        }>
          {products.map((p, idx) => (
            <div key={p.id} className="grid grid-cols-5 gap-2 items-end">
              <Field label="Product Name">
                <input {...register(`products.${idx}.name` as const)} className="input" />
              </Field>
              <Field label="Project Name">
                <input {...register(`products.${idx}.project` as const)} className="input" />
              </Field>
              <Field label="Qty">
                <input type="number" min="1" step="1" inputMode="numeric" onKeyDown={onlyDigitKeys} {...register(`products.${idx}.qty` as const, { valueAsNumber: true })} className="input" />
              </Field>
              <Field label="Invoice Value">
                <input type="number" step="0.01" min="0" inputMode="decimal" onKeyDown={blockNonNumericKeys} {...register(`products.${idx}.invoiceValue` as const, { valueAsNumber: true })} className="input" />
              </Field>
              <div className="flex gap-1">
                <Field label="E-Way Bill No">
                  <input {...register(`products.${idx}.ewayBillNo` as const)} className="input" />
                </Field>
                {products.length > 1 && <button type="button" onClick={() => removeProduct(idx)} className="text-danger text-xs px-2 self-center">×</button>}
              </div>
            </div>
          ))}
        </Section>

        <Section title="Weight & Dimensions">
          <div className="grid grid-cols-4 gap-3">
            <Field label="Weight (kg)">
              <input type="number" step="0.001" min="0" inputMode="decimal" onKeyDown={blockNonNumericKeys} {...register('weight', { valueAsNumber: true })} className="input input-num" />
            </Field>
            <Field label="Length (cm)">
              <input type="number" step="0.1" min="0" inputMode="decimal" onKeyDown={blockNonNumericKeys} {...register('length', { valueAsNumber: true })} className="input input-num" />
            </Field>
            <Field label="Breadth (cm)">
              <input type="number" step="0.1" min="0" inputMode="decimal" onKeyDown={blockNonNumericKeys} {...register('breadth', { valueAsNumber: true })} className="input input-num" />
            </Field>
            <Field label="Height (cm)">
              <input type="number" step="0.1" min="0" inputMode="decimal" onKeyDown={blockNonNumericKeys} {...register('height', { valueAsNumber: true })} className="input input-num" />
            </Field>
          </div>
          <div className="text-xs text-slate-600 flex items-center flex-wrap gap-x-4 gap-y-1">
            <span>Volumetric weight (L×B×H/5000): <b className="text-slate-800">{volWeight}</b> kg</span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800">
              Charged Weight (higher): <b className="font-mono">{chargedWeight}</b> kg
            </span>
          </div>
          <Field label="Is Fragile?">
            <select {...register('isFragile')} className="input">
              <option value="">Select</option>
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </select>
          </Field>
        </Section>

        <OrderValidationPanel orderType="B2C" fields={normalizeB2c(watch())} />

        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => nav(-1)} className="px-4 py-2 border border-red-300 text-danger text-sm rounded-lg hover:bg-red-50">CANCEL</button>
          <button type="submit" disabled={create.isPending}
            className="px-4 py-2 bg-emerald-700 text-white text-sm rounded-lg font-medium hover:bg-emerald-800 transition disabled:opacity-60 flex items-center gap-2">
            {create.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children, right }: { title: string; children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {right}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children, error }: { label: string; children: React.ReactNode; error?: string }) {
  const required = label.trim().endsWith('*');
  const base = required ? label.replace(/\s*\*\s*$/, '') : label;
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{base}{required && <span className="text-rose-500 ml-0.5">*</span>}</label>
      {children}
      {error && <p className="text-danger text-xs mt-1">{error}</p>}
    </div>
  );
}
